import React, { useState, useEffect } from "react";
import { MinusCircleOutlined, PlusCircleOutlined, DeleteOutlined, InfoCircleOutlined, UploadOutlined } from "@ant-design/icons";
import Popup from "../../../../components/Popup";
import "./styles.css";

export default function CategoryPopup({
  loading = false,
  visible = false,
  title = "",
  data = {},
  categoryOptions = [],
  categorySections = [],
  uploadImage = false,
  subCategory = false,
  isSubCategoryGroup = false,
  saveHandler,
  cancelHandler,
}) {
  // Initial form state based on data
  const [formValues, setFormValues] = useState({
    category_id: data.category_id || "",
    category_section_id: data.category_section_id || "",
    name: data.sub_category_group_name || data.sub_category_name || data.name || "",
    description: data.description || "",
    priority: data.priority || "",
    is_active: data.is_active !== undefined
      ? data.is_active === 1 || data.is_active === true
      : true
  });
  
  // Form validation state
  const [formErrors, setFormErrors] = useState({});
  // Groups state (for subcategory groups)
  const [groups, setGroups] = useState([]);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);

  // Initialize groups if data has them
  useEffect(() => {
    if (data.sub_category_groups && data.sub_category_groups.length > 0) {
      setGroups(data.sub_category_groups);
    }
    // Update form values when data changes
    setFormValues({
      category_id: data.category_id || "",
      category_section_id: data.category_section_id || "",
      name: data.sub_category_group_name || data.sub_category_name || data.name || "",
      description: data.description || "",
      priority: data.priority || "",
      is_active: data.is_active !== undefined
        ? data.is_active === 1 || data.is_active === true
        : true
    });
  }, [data]);

  // Handle input change
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormValues({
      ...formValues,
      [name]: type === "checkbox" ? checked : value,
    });
  };

  // Handle image change
  const handleImageChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setImageFile(file);
      setImagePreview(URL.createObjectURL(file));
    }
  };

  // Handle image drop
  const handleImageDrop = (e) => {
    e.preventDefault();
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const file = e.dataTransfer.files[0];
      setImageFile(file);
      setImagePreview(URL.createObjectURL(file));
    }
  };

  // Remove image
  const removeImage = () => {
    setImageFile(null);
    setImagePreview(null);
  };

  // Validate form
  const validateForm = () => {
    const errors = {};
    
    if (subCategory && !data.sub_category_name && !formValues.category_id) {
      errors.category_id = "Please select a category";
    }
    
    if (!formValues.name) {
      errors.name = subCategory 
        ? "Please enter the name of sub category"
        : "Please enter the name of category";
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Add a new group
  const addGroup = () => {
    setGroups([...groups, { name: "", description: "", file: null }]);
  };

  // Remove a group
  const removeGroup = (index) => {
    const newGroups = [...groups];
    newGroups.splice(index, 1);
    setGroups(newGroups);
  };

  // Handle group input change
  const handleGroupChange = (index, field, value) => {
    const newGroups = [...groups];
    newGroups[index][field] = value;
    setGroups(newGroups);
  };

  // Handle group file change
  const handleGroupFileChange = (index, e) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      const newGroups = [...groups];
      newGroups[index].file = file;
      newGroups[index].preview = URL.createObjectURL(file);
      setGroups(newGroups);
    }
  };

  // Remove group image
  const removeGroupImage = (index) => {
    const newGroups = [...groups];
    newGroups[index].file = null;
    newGroups[index].preview = null;
    setGroups(newGroups);
  };

  // Handle form submission
  const handleSubmit = () => {
    if (validateForm()) {
      const formData = {
        ...formValues,
        file: imageFile,
        sub_category_groups: groups.map(group => ({
          name: group.name,
          description: group.description,
          file: group.file
        }))
      };
      
      saveHandler(formData);
    }
  };

  return (
    <Popup
      title={title}
      visible={visible}
      loading={loading}
      cancelHandler={() => cancelHandler()}
      saveHandler={handleSubmit}
    >
      <form className="category-form">
        {/* Category Selection (only for subcategories) */}
        {subCategory && !data.sub_category_name && (
          <div className="form-group">
            <label htmlFor="category_id">Category</label>
            <select
              id="category_id"
              name="category_id"
              value={formValues.category_id}
              onChange={handleInputChange}
              className={formErrors.category_id ? "input-error" : ""}
            >
              <option value="">Select a category</option>
              {categoryOptions.map((category) => (
                <option key={category.category_id} value={category.category_id}>
                  {category.name}
                </option>
              ))}
            </select>
            {formErrors.category_id && (
              <div className="error-message">{formErrors.category_id}</div>
            )}
          </div>
        )}

        {/* Category Section Selection (only for categories, not subcategories) */}
        {!subCategory && categorySections && categorySections.length > 0 && (
          <div className="form-group full-width">
            <label htmlFor="category_section_id">Category Section</label>
            <select
              id="category_section_id"
              name="category_section_id"
              value={formValues.category_section_id}
              onChange={handleInputChange}
            >
              <option value="">Select a category section (optional)</option>
              {categorySections.map((section) => (
                <option key={section.category_section_id} value={section.category_section_id}>
                  {section.name}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Simple Image Upload - First Row */}
        {uploadImage && (
          <div className="form-group full-width">
            <div 
              className={`file-drop-area ${imagePreview ? 'has-image' : ''}`}
              onDragOver={(e) => e.preventDefault()}
              onDrop={handleImageDrop}
            >
              {!imagePreview ? (
                <>
                  <div className="file-drop-content">
                    <div className="file-icon-container">
                      <UploadOutlined style={{ fontSize: '32px', color: '#bfbfbf' }} />
                    </div>
                    <div className="file-message">
                      <p className="drag-message">Drag Your File Here</p>
                      <p className="format-message">JPG, PNG, or GIF format only</p>
                    </div>
                  </div>
                  <input 
                    type="file" 
                    className="file-input"
                    accept="image/*"
                    onChange={handleImageChange}
                  />
                </>
              ) : (
                <div className="image-preview-container">
                  <img src={imagePreview} alt="Preview" className="image-preview" />
                  <button 
                    type="button" 
                    className="remove-image-btn" 
                    onClick={removeImage}
                  >
                    <DeleteOutlined /> Remove
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Name Field - Second Row (Full Width) */}
        <div className="form-group full-width">
          <label htmlFor="name">
            Name <span className="required">*</span>
          </label>
          <input
            type="text"
            id="name"
            name="name"
            value={formValues.name}
            onChange={handleInputChange}
            placeholder={subCategory ? "Ex. Milk" : "Ex. Dairy Products"}
            className={formErrors.name ? "input-error" : ""}
          />
          {formErrors.name && (
            <div className="error-message">{formErrors.name}</div>
          )}
        </div>

        {/* Description Field */}
        <div className="form-group full-width">
          <label htmlFor="description">Description</label>
          <textarea
            id="description"
            name="description"
            value={formValues.description}
            onChange={handleInputChange}
            placeholder={
              subCategory
                ? "Ex. All types of Milk like toned, processed."
                : "Ex. All types of dairy products like milk, bread, egg."
            }
            maxLength={255}
            rows={3}
          />
          <div className="char-count">{formValues.description.length}/255</div>
        </div>

        {/* Priority Field - Only show for categories (hide for subcategories and groups) */}
        {!subCategory && !isSubCategoryGroup && (
          <div className="form-group">
            <label htmlFor="priority">Priority</label>
            <input
              type="number"
              id="priority"
              name="priority"
              value={formValues.priority}
              onChange={handleInputChange}
              placeholder="Ex. 1, 2, 3..."
              min={0}
            />
            <div className="help-text">Lower numbers appear first. Leave empty for no specific priority.</div>
          </div>
        )}

        {/* Status Toggle */}
        <div className="form-group status-toggle">
          <label htmlFor="is_active">Status</label>
          <div className="toggle-container">
            <input
              type="checkbox"
              id="is_active"
              name="is_active"
              checked={formValues.is_active}
              onChange={handleInputChange}
            />
            <label htmlFor="is_active" className="toggle-label">
              <span className="toggle-inner"></span>
              <span className="toggle-switch"></span>
            </label>
            <span className="toggle-text">
              {formValues.is_active ? "Active" : "Inactive"}
            </span>
          </div>
        </div>

        {/* Groups Section (for subcategories) */}
        {uploadImage && subCategory && (
          <div className="groups-section">
            <div className="section-header">
              <h3>Groups</h3>
              <p className="help-text">Add groups to categorize products further (optional)</p>
            </div>

            {groups.map((group, index) => (
              <div key={index} className="group-item">
                <div className="group-row">
                  {/* Group Image */}
                  <div className="group-image">
                    <div className={`file-drop-area small ${group.preview ? 'has-image' : ''}`}>
                      {!group.preview ? (
                        <>
                          <div className="file-drop-content">
                            <UploadOutlined style={{ fontSize: '20px', color: '#bfbfbf' }} />
                          </div>
                          <input 
                            type="file" 
                            className="file-input"
                            accept="image/*"
                            onChange={(e) => handleGroupFileChange(index, e)}
                          />
                        </>
                      ) : (
                        <div className="image-preview-container">
                          <img src={group.preview} alt="Preview" className="image-preview" />
                          <button 
                            type="button" 
                            className="remove-image-btn small" 
                            onClick={() => removeGroupImage(index)}
                          >
                            <DeleteOutlined />
                          </button>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Group Details */}
                  <div className="group-details">
                    <div className="form-group">
                      <label htmlFor={`group-name-${index}`}>Name <span className="required">*</span></label>
                      <input
                        id={`group-name-${index}`}
                        type="text"
                        value={group.name || ""}
                        onChange={(e) => handleGroupChange(index, "name", e.target.value)}
                        placeholder="Ex. Toned Milk"
                      />
                    </div>
                    <div className="form-group">
                      <label htmlFor={`group-desc-${index}`}>Description</label>
                      <input
                        id={`group-desc-${index}`}
                        type="text"
                        value={group.description || ""}
                        onChange={(e) => handleGroupChange(index, "description", e.target.value)}
                        placeholder="Ex. All types and brands of toned milk."
                      />
                    </div>
                  </div>

                  {/* Remove Button */}
                  <button
                    type="button"
                    className="remove-group-btn"
                    onClick={() => removeGroup(index)}
                  >
                    <MinusCircleOutlined />
                  </button>
                </div>
              </div>
            ))}

            {/* Add Group Button */}
            <button
              type="button"
              className="add-group-btn"
              onClick={addGroup}
            >
              <PlusCircleOutlined /> Add Group
            </button>
          </div>
        )}
      </form>
    </Popup>
  );
}